import Api from "@ciq/shared/api/api"

/**
 * @typedef {Object} JobEntry
 * @property {string} job - Job type name
 * @property {string|null} job_date - Job date (null if missing/skipped)
 * @property {string|null} id - Job ID (null if missing/skipped)
 * @property {string|null} glue_id - Glue job ID (null if missing/skipped)
 * @property {string} status - Job status (Finished, Error, Running, Staged, Queued, Missing, Stale, Skipped, No Data)
 * @property {string} [skip_reason] - Reason why job was skipped (e.g., "Weekdays only")
 */

/**
 * @typedef {Object} DateSummary
 * @property {number} number_of_runs - Number of job runs for this date
 * @property {Array<string>} labels - Status labels (e.g., "Completed", "Error")
 */

/**
 * @typedef {Object} DateFlags
 * @property {boolean} has_running - Has running/queued/staged jobs
 * @property {boolean} has_failures - Has Error or No Data jobs
 * @property {boolean} has_missing - Has expected jobs that didn't run
 * @property {boolean} has_stale - Has jobs that need re-running due to upstream changes
 */

/**
 * @typedef {Object} DateCard
 * @property {string} job_date - Date in ISO format (YYYY-MM-DD)
 * @property {string} date_label - Human-readable date label
 * @property {DateSummary} summary - Summary with run count and labels
 * @property {DateFlags} flags - Status flags for quick filtering
 * @property {Array<JobEntry>} jobs - Flat list of job statuses
 */

/**
 * @typedef {Object} FilterCounts
 * @property {number} all - Total date count
 * @property {number} running - Dates with running jobs
 * @property {number} failures - Dates with failed jobs
 * @property {number} missing - Dates with missing jobs
 * @property {number} stale - Dates with stale jobs
 * @property {number} issues - Dates with any issue (failures, missing, or stale)
 */

/**
 * @typedef {Object} GroupedJobHistoryResponse
 * @property {Array<DateCard>} dates - Array of date cards with job data
 * @property {FilterCounts} filter_counts - Counts for each filter category
 */

/**
 * Fetch grouped job history for a school, organized by date and chain.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {Object} params - Query parameters
 * @param {number} [params.limit] - Number of dates to return (default 14, max 30)
 * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
 * @returns {Promise<GroupedJobHistoryResponse>} A promise that resolves to grouped job history
 */
export async function fetchGroupedJobHistory(schoolId, params, signal) {
  const response = await Api.get(`schools/${schoolId}/spark/runs/grouped`, {
    signal,
    params,
  })
  return response.data
}

/**
 * @typedef {Object} SchoolIssue
 * @property {string} school_id - School identifier
 * @property {string} school_name - School display name
 * @property {Object} issues - Issue counts by type
 * @property {number} issues.missing - Count of missing jobs
 * @property {number} issues.stale - Count of stale jobs
 * @property {number} issues.error - Count of error jobs
 * @property {number} issues.no_data - Count of no data jobs
 * @property {number} total_issues - Total issue count
 */

/**
 * @typedef {Object} IssuesSummaryResponse
 * @property {Array<SchoolIssue>} schools - Schools with issues
 * @property {number} total_schools_with_issues - Total count
 * @property {number} period_days - Lookback period
 */

/**
 * Fetch cross-school issues summary.
 *
 * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
 * @returns {Promise<IssuesSummaryResponse>} A promise that resolves to issues summary
 */
export async function fetchIssuesSummary(signal) {
  const response = await Api.get("spark/schools/issues-summary", { signal })
  return response.data
}
